import { create } from 'zustand'
import {
	LearningLanguage,
	User,
	Chat,
	ChatEntry,
	LibraryOverview,
	LibraryVocabulary,
	PracticeOverview,
	LibraryCollection,
} from '@/models.ts'
import { TextToSpeech } from '@capacitor-community/text-to-speech'
import api from '@/api.ts'
import usePreferences from '@/usePreferences.ts'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import i18n from 'i18next'
import * as Sentry from '@sentry/react'

interface GlobalStore {
	user: User | null
	setUser: (user: User | null) => void
	isAuthLoaded: boolean
	setIsAuthLoaded: (authLoaded: boolean) => void
	isTrialExpired: boolean
	setIsTrialExpired: (isTrialExpired: boolean) => void
	activeChat: Chat | null
	setActiveChat: (activeChat: Chat | null) => void
	lastActiveChatTappedTimestamp: number | null
	setLastActiveChatTappedTimestamp: (lastActiveChatTappedTimestamp: number) => void
	latestNewChat: Chat | null
	setLatestNewChat: (latestNewChat: Chat) => void
	chatEntries: ChatEntry[]
	addChatEntry: (entry: ChatEntry) => void
	setChatEntries: (entries: ChatEntry[]) => void
	updateChatEntryByFieldName: (field: string, value: any, updatedEntry: ChatEntry) => void
	removeChatEntryByFieldName: (field: string, value: any) => void
	libraryOverview: LibraryOverview | null
	setLibraryOverview: (libraryOverview: LibraryOverview) => void
	practiceOverview: PracticeOverview | null
	setPracticeOverview: (practiceOverview: PracticeOverview) => void
	isAwaitingAiResponse: boolean
	setIsAwaitingAiResponse: (isAwaitingAiResponse: boolean) => void
	availableLearningLanguages: Record<string, LearningLanguage>
	setAvailableLearningLanguages: (availableLanguages: Record<string, LearningLanguage>) => void
	learningLanguageCode: string
	setLearningLanguageCode: (learningLanguageCode: string) => void
	availableAppLanguages: Record<string, LearningLanguage>
	defaultAppLanguageCode: string
	appLanguageCode: string
	setAppLanguageCode: (appLanguageCode: string) => void
	changeAppLanguage: (key: string) => Promise<void>
	chatHistoryList: Chat[]
	setChatHistoryList: (chatHistoryList: Chat[]) => void
	isLoadingChatHistory: boolean
	setIsLoadingChatHistory: (isLoadingChatHistory: boolean) => void
	isChatHistoryLoaded: boolean
	setIsChatHistoryLoaded: (isChatHistoryLoaded: boolean) => void
	loadChatHistoryList: () => Promise<void>
	isTTSSupported: boolean
	setIsTTSSupported: (isTTSSupported: boolean) => void
	changeLearningLanguage: (key: string) => Promise<void>
	loadLibraryOverview: () => Promise<void>
	isLibraryOverviewLoading: boolean
	setIsLibraryOverviewLoading: (isLibraryOverviewLoading: boolean) => void
	loadPracticeOverview: () => Promise<void>
	isPracticeOverviewLoading: boolean
	setIsPracticeOverviewLoading: (isPracticeOverviewLoading: boolean) => void
	loadChatAsActiveChat: (id: number) => Promise<void>
	isLoadingChat: boolean
	setIsLoadingChat: (isLoadingChat: boolean) => void
	resetChat: (reloadLibrary: boolean) => void
	allSavedLibraryVocabulary: LibraryVocabulary[] | null
	loadAllSavedLibraryVocabulary: () => Promise<void>
	currentPracticeStatus: string
	setCurrentPracticeStatus: (currentPracticeStatus: string) => void
	appNewsHtml: string
	setAppNewsHtml: (newsHtml: string) => void
	isNetworkOnline: boolean
	setIsNetworkOnline: (isNetworkOnline: boolean) => void
	appVersion: string
	setAppVersion: (appVersion: string) => void
	isModalVisible: boolean
	setIsModalVisible: (isModalVisible: boolean) => void
	isSheetVisible: boolean
	setIsSheetVisible: (isSheetVisible: boolean) => void
	tips: { title: string; text: string }[]
	setTips: (tips: { title: string; text: string }[]) => void
	libraryCollectionsList: LibraryCollection[]
	setLibraryCollectionsList: (libraryCollectionsList: LibraryCollection[]) => void
	loadLibraryCollectionsList: () => Promise<void>
	isLibraryCollectionsListLoading: boolean
}

const useGlobalStore = create<GlobalStore>((set, get) => ({
	user: null,
	setUser: (user: User | null) => set({ user: user }),
	isAuthLoaded: false,
	setIsAuthLoaded: (isAuthLoaded: boolean) => set({ isAuthLoaded }),
	isTrialExpired: false,
	setIsTrialExpired: (isTrialExpired: boolean) => set({ isTrialExpired }),
	activeChat: null,
	setActiveChat: (activeChat: Chat | null) => set({ activeChat: activeChat }),
	lastActiveChatTappedTimestamp: null,
	setLastActiveChatTappedTimestamp: (lastActiveChatTappedTimestamp: number) => set({ lastActiveChatTappedTimestamp }),
	latestNewChat: null,
	setLatestNewChat: (latestNewChat: Chat) => set({ latestNewChat: latestNewChat }),
	chatEntries: [],
	addChatEntry: (entry: ChatEntry) =>
		set((state) => ({
			chatEntries: [...state.chatEntries, entry],
		})),
	setChatEntries: (entries: ChatEntry[]) => set({ chatEntries: entries }),
	updateChatEntryByFieldName: (field: string, value: any, updatedEntry: ChatEntry) =>
		set((state) => ({
			chatEntries: state.chatEntries.map((entry) =>
				// @ts-ignore
				entry[field] === value ? updatedEntry : entry,
			),
		})),
	removeChatEntryByFieldName: (field: string, value: any) =>
		set((state) => ({
			chatEntries: state.chatEntries.filter(
				(entry) =>
					// @ts-ignore
					entry[field] !== value,
			),
		})),
	libraryOverview: null,
	setLibraryOverview: (libraryOverview: LibraryOverview) => set({ libraryOverview: libraryOverview }),
	practiceOverview: null,
	setPracticeOverview: (practiceOverview: PracticeOverview) => set({ practiceOverview: practiceOverview }),
	isAwaitingAiResponse: false,
	setIsAwaitingAiResponse: (isAwaitingAiResponse: boolean) => set({ isAwaitingAiResponse }),
	availableLearningLanguages: {
		'en-US': {
			name: 'English',
			name_en: 'English',
			icon: 'us',
			dir: 'ltr',
		},
		'da-DK': {
			name: 'Dansk',
			name_en: 'Danish',
			icon: 'dk',
			dir: 'ltr',
		},
		'de-DE': {
			name: 'Deutsch',
			name_en: 'German',
			icon: 'de',
			dir: 'ltr',
		},
		'fr-FR': {
			name: 'Français',
			name_en: 'French',
			icon: 'fr',
			dir: 'ltr',
		},
		'sv-SE': {
			name: 'Svenska',
			name_en: 'Swedish',
			icon: 'se',
			dir: 'ltr',
		},
		'es-ES': {
			name: 'Español',
			name_en: 'Spanish',
			icon: 'es',
			dir: 'ltr',
		},
		'it-IT': {
			name: 'Italiano',
			name_en: 'Italian',
			icon: 'it',
			dir: 'ltr',
		},
		'nl-NL': {
			name: 'Nederlands',
			name_en: 'Dutch',
			icon: 'nl',
			dir: 'ltr',
		},
		'pt-PT': {
			name: 'Português',
			name_en: 'Portuguese',
			icon: 'pt',
			dir: 'ltr',
		},
		'el-GR': {
			name: 'Ελληνικά',
			name_en: 'Greek',
			icon: 'gr',
			dir: 'ltr',
		},
		'cs-CZ': {
			name: 'Čeština',
			name_en: 'Czech',
			icon: 'cz',
			dir: 'ltr',
		},
		'tr-TR': {
			name: 'Türkçe',
			name_en: 'Turkish',
			icon: 'tr',
			dir: 'ltr',
		},
		'no-NO': {
			name: 'Norsk',
			name_en: 'Norwegian',
			icon: 'no',
			dir: 'ltr',
		},
		'fi-FI': {
			name: 'Suomi',
			name_en: 'Finnish',
			icon: 'fi',
			dir: 'ltr',
		},
		'pl-PL': {
			name: 'Polski',
			name_en: 'Polish',
			icon: 'pl',
			dir: 'ltr',
		},
		'hu-HU': {
			name: 'Magyar',
			name_en: 'Hungarian',
			icon: 'hu',
			dir: 'ltr',
		},
		'ro-RO': {
			name: 'Română',
			name_en: 'Romanian',
			icon: 'ro',
			dir: 'ltr',
		},
		'ru-RU': {
			name: 'Русский',
			name_en: 'Russian',
			icon: 'ru',
			dir: 'ltr',
		},
		'sk-SK': {
			name: 'Slovenčina',
			name_en: 'Slovak',
			icon: 'sk',
			dir: 'ltr',
		},
		'sl-SI': {
			name: 'Slovenščina',
			name_en: 'Slovenian',
			icon: 'si',
			dir: 'ltr',
		},
		'sr-RS': {
			name: 'Српски',
			name_en: 'Serbian',
			icon: 'rs',
			dir: 'ltr',
		},
		'bg-BG': {
			name: 'Български',
			name_en: 'Bulgarian',
			icon: 'bg',
			dir: 'ltr',
		},
		'hr-HR': {
			name: 'Hrvatski',
			name_en: 'Croatian',
			icon: 'hr',
			dir: 'ltr',
		},
		'sq-AL': {
			name: 'Shqip',
			name_en: 'Albanian',
			icon: 'al',
			dir: 'ltr',
		},
		'lt-LT': {
			name: 'Lietuvių',
			name_en: 'Lithuanian',
			icon: 'lt',
			dir: 'ltr',
		},
		'et-EE': {
			name: 'Eesti',
			name_en: 'Estonian',
			icon: 'ee',
			dir: 'ltr',
		},
		'lv-LV': {
			name: 'Latviešu',
			name_en: 'Latvian',
			icon: 'lv',
			dir: 'ltr',
		},
		'zh-CN': {
			name: '简体中文',
			name_en: 'Chinese (Simplified)',
			icon: 'cn',
			dir: 'ltr',
		},
		'ja-JP': {
			name: '日本語',
			name_en: 'Japanese',
			icon: 'jp',
			dir: 'ltr',
		},
		'ko-KR': {
			name: '한국어',
			name_en: 'Korean',
			icon: 'kr',
			dir: 'ltr',
		},
		'uk-UA': {
			name: 'Українська',
			name_en: 'Ukrainian',
			icon: 'ua',
			dir: 'ltr',
		},
		'fa-IR': {
			name: 'فارسی',
			name_en: 'Persian',
			icon: 'ir',
			dir: 'rtl',
		},
		'ar-SA': {
			name: 'العربية',
			name_en: 'Arabic',
			icon: 'sa',
			dir: 'rtl',
		},
	},
	setAvailableLearningLanguages: (availableLanguages: Record<string, LearningLanguage>) =>
		set(() => ({
			availableLearningLanguages: availableLanguages,
		})),
	learningLanguageCode: '',
	setLearningLanguageCode: (learningLanguageApp: string) => set({ learningLanguageCode: learningLanguageApp }),
	availableAppLanguages: {
		'en-US': {
			name: 'English',
			name_en: 'English',
			icon: 'us',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/en'),
		},
		'de-DE': {
			name: 'Deutsch',
			name_en: 'German',
			icon: 'de',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/de'),
		},
		'fr-FR': {
			name: 'Français',
			name_en: 'French',
			icon: 'fr',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/fr'),
		},
		'es-ES': {
			name: 'Español',
			name_en: 'Spanish',
			icon: 'es',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/es'),
		},
		'it-IT': {
			name: 'Italiano',
			name_en: 'Italian',
			icon: 'it',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/it'),
		},
		'ru-RU': {
			name: 'Русский',
			name_en: 'Russian',
			icon: 'ru',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/ru'),
		},
		'tr-TR': {
			name: 'Türkçe',
			name_en: 'Turkish',
			icon: 'tr',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/tr'),
		},
		'uk-UA': {
			name: 'Українська',
			name_en: 'Ukrainian',
			icon: 'ua',
			dir: 'ltr',
			dayjs_locale: async () => import('dayjs/locale/uk'),
		},
		'fa-IR': {
			name: 'فارسی',
			name_en: 'Persian',
			icon: 'ir',
			dir: 'rtl',
			dayjs_locale: async () => import('dayjs/locale/fa'),
		},
		'ar-SA': {
			name: 'العربية',
			name_en: 'Arabic',
			icon: 'sa',
			dir: 'rtl',
			dayjs_locale: async () => import('dayjs/locale/ar'),
		},
	},
	defaultAppLanguageCode: 'en-US',
	appLanguageCode: 'en-US',
	setAppLanguageCode: (appLanguageCode: string) => set({ appLanguageCode: appLanguageCode }),
	changeAppLanguage: async (key: string) => {
		get().setAppLanguageCode(key)
		if (key !== undefined && key !== null && key !== '') {
			const preferences = usePreferences.getState()
			preferences.setItem('appLanguageCode', key).then()
			if (get().availableAppLanguages[key] !== undefined) {
				if (get().availableAppLanguages[key].dayjs_locale !== undefined) {
					await get().availableAppLanguages[key].dayjs_locale()
					dayjs.locale(key)
				} else {
					console.warn(`Dayjs Locale '${key}' is not supported.`)
				}
			}
		}
	},
	chatHistoryList: [],
	setChatHistoryList: (chatHistoryList: Chat[]) => set({ chatHistoryList }),
	isLoadingChatHistory: false,
	setIsLoadingChatHistory: (isLoadingChatHistory: boolean) => set({ isLoadingChatHistory }),
	isChatHistoryLoaded: false,
	setIsChatHistoryLoaded: (isChatHistoryLoaded: boolean) => set({ isChatHistoryLoaded }),
	loadChatHistoryList: async () => {
		const currentLearningLanguage = get().learningLanguageCode
		set({ isLoadingChatHistory: true })
		const data: Chat[] = await api(`/v1/chat/list/${currentLearningLanguage}`, {
			method: 'GET',
		}).finally(() => {
			set({ isLoadingChatHistory: false })
		})
		set({ chatHistoryList: data })
		set({ isChatHistoryLoaded: true })
	},
	// appTokens: {},
	// setAppToken: (key: string, value: string) =>
	// 	set((state) => ({
	// 		appTokens: {
	// 			...state.appTokens,
	// 			[key]: value,
	// 		},
	// 	})),
	isTTSSupported: true,
	setIsTTSSupported: (isTTSSupported: boolean) => set({ isTTSSupported }),
	changeLearningLanguage: async (key: string) => {
		const currentLearningLanguage = get().learningLanguageCode
		get().setLearningLanguageCode(key)
		// reset chat
		get().setActiveChat(null)
		get().setChatEntries([])
		if (get().user) {
			get().loadChatHistoryList()
			get().loadPracticeOverview()
			get().loadLibraryOverview()
		}
		const { supported } = await TextToSpeech.isLanguageSupported({ lang: key })
		get().setIsTTSSupported(supported)

		if (key !== undefined && key !== null && key !== '' && key !== currentLearningLanguage) {
			const preferences = usePreferences.getState()
			preferences.setItem('learningLanguageCode', key).then()
		}
	},
	loadLibraryOverview: async () => {
		set({ isLibraryOverviewLoading: true })
		const libraryOverview: LibraryOverview = await api(`/v1/library/overview/${get().learningLanguageCode}`, {
			method: 'GET',
		})
			.finally(() => set({ isLibraryOverviewLoading: false }))
			.catch(() => {})
		get().setLibraryOverview(libraryOverview)
	},
	isLibraryOverviewLoading: true,
	setIsLibraryOverviewLoading: (isLibraryOverviewLoading: boolean) => set({ isLibraryOverviewLoading }),
	loadPracticeOverview: async () => {
		set({ isPracticeOverviewLoading: true })
		const practiceOverview: PracticeOverview = await api(`/v1/practice/overview/${get().learningLanguageCode}`, {
			method: 'GET',
		})
			.finally(() => set({ isPracticeOverviewLoading: false }))
			.catch(() => {})
		get().setPracticeOverview(practiceOverview)
	},
	isPracticeOverviewLoading: true,
	setIsPracticeOverviewLoading: (isPracticeOverviewLoading: boolean) => set({ isPracticeOverviewLoading }),
	loadChatAsActiveChat: async (id: number) => {
		set({ isLoadingChat: true })
		try {
			const data: Chat = await api(`/v1/chat/${id}`, {
				method: 'GET',
			})
			get().setActiveChat(data)
			get().setChatEntries(data.entries ?? [])
		} catch (error: any) {
			// 401/402/419 and offline are already surfaced by api.ts
			if (![401, 402, 419].includes(error?.response?.status)) {
				toast(error?.data?.message ?? i18n.t('Something went wrong'))
			}
			Sentry.captureException(error)
		} finally {
			set({ isLoadingChat: false })
		}
	},
	isLoadingChat: false,
	setIsLoadingChat: (isLoadingChat: boolean) => set({ isLoadingChat }),
	resetChat: async (reloadLibrary = false) => {
		set(() => ({
			activeChat: null,
			chatEntries: [],
			lastActiveChatTappedTimestamp: Date.now(),
		}))
		if (reloadLibrary) {
			await get().loadLibraryOverview()
		}
	},
	allSavedLibraryVocabulary: null,
	loadAllSavedLibraryVocabulary: async () => {
		const libraryVocabularies: LibraryVocabulary[] = await api(
			`/v1/library/vocabulary/list/${get().learningLanguageCode}`,
			{
				method: 'GET',
			},
		).catch(() => {})
		set({ allSavedLibraryVocabulary: libraryVocabularies })
	},
	currentPracticeStatus: '',
	setCurrentPracticeStatus: (currentPracticeStatus: string) => set({ currentPracticeStatus }),
	appNewsHtml: '',
	setAppNewsHtml: (appNewsHtml: string) => set({ appNewsHtml }),
	isNetworkOnline: true,
	setIsNetworkOnline: (isNetworkOnline: boolean) => set({ isNetworkOnline }),
	appVersion: '',
	setAppVersion: (appVersion: string) => set({ appVersion }),
	isModalVisible: false,
	setIsModalVisible: (isModalVisible: boolean) => set({ isModalVisible }),
	isSheetVisible: false,
	setIsSheetVisible: (isSheetVisible: boolean) => set({ isSheetVisible }),
	tips: [],
	setTips: (tips: { title: string; text: string }[]) => set({ tips }),
	libraryCollectionsList: [],
	setLibraryCollectionsList: (libraryCollectionsList: LibraryCollection[]) => set({ libraryCollectionsList }),
	loadLibraryCollectionsList: async () => {
		set({ isLibraryCollectionsListLoading: true })
		const data: LibraryCollection[] = await api(`/v2/library/collections/list`, {
			method: 'GET',
		}).finally(() => set({ isLibraryCollectionsListLoading: false }))
		set({ libraryCollectionsList: data })
	},
	isLibraryCollectionsListLoading: true,
}))

export default useGlobalStore
